import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, MapPin, Briefcase, MessageSquare, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { profileAPI, interestAPI, premiumAPI } from '../services/api';
import BackButton from '../components/ui/BackButton';
import DynamicProfileCard from '../components/DynamicProfileCard';

const ShortlistPage = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [shortlistedProfiles, setShortlistedProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sentInterests, setSentInterests] = useState([]);
  const [receivedInterests, setReceivedInterests] = useState([]);
  const [premiumStatus, setPremiumStatus] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchShortlist();
      fetchInterests();
      loadPremiumStatus();
    }
  }, [isAuthenticated, user]);

  const loadPremiumStatus = async () => {
    try {
      const data = await premiumAPI.getStatus();
      setPremiumStatus(data);
    } catch (error) {
      console.error('Failed to load premium status:', error);
    }
  };

  const fetchInterests = async () => {
    try {
      const [sent, received] = await Promise.all([
        interestAPI.getSent(),
        interestAPI.getReceived()
      ]);
      setSentInterests(sent.interests || []);
      setReceivedInterests(received.interests || []);
    } catch (error) {
      console.error('Error fetching interests:', error);
    }
  };

  const fetchShortlist = async () => {
    try {
      setLoading(true);
      const response = await profileAPI.getShortlist();
      setShortlistedProfiles(response.shortlist || []);
    } catch (error) {
      console.error('Error fetching shortlist:', error);
      toast({
        title: "Error",
        description: "Failed to load shortlist. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromShortlist = async (profileId, profileName) => {
    try {
      await profileAPI.removeFromShortlist(profileId);
      setShortlistedProfiles(shortlistedProfiles.filter(p => p.id !== profileId));
      toast({
        title: "Removed from Shortlist",
        description: `${profileName} has been removed from your shortlist`,
      });
    } catch (error) {
      console.error('Error removing from shortlist:', error);
      toast({
        title: "Error",
        description: "Failed to remove from shortlist. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSwipeInterest = async (profileId) => {
    try {
      await profileAPI.sendInterest(profileId);
      const updatedSent = await interestAPI.getSent();
      setSentInterests(updatedSent.interests || []);
      toast({
        title: "Interest Sent! 💖",
        description: "Your interest has been sent successfully",
      });
    } catch (error) {
      console.error('Error sending interest:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send interest",
        variant: "destructive",
      });
    }
  };

  // Handle accepting a received interest
  const handleAcceptInterest = async (profileId) => {
    try {
      // Find the received interest for this profile
      const receivedInterest = receivedInterests.find(int => {
        const senderId = int.sender?.id || int.senderId;
        return senderId === profileId;
      });

      if (!receivedInterest) {
        toast({
          title: "Error",
          description: "Interest not found",
          variant: "destructive",
        });
        return;
      }

      // Accept the interest
      await interestAPI.respond(receivedInterest.id, 'accepted');
      
      // Refetch received interests to update UI
      const updatedReceived = await interestAPI.getReceived();
      setReceivedInterests(updatedReceived.interests || []);
      
      toast({
        title: "Interest Accepted! 💚",
        description: "You can now message this person",
      });
    } catch (error) {
      console.error('Error accepting interest:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to accept interest",
        variant: "destructive",
      });
    }
  };

  // Handle interest click - either send new interest or accept received interest
  const handleInterestAction = async (profileId) => {
    const interestStatus = getInterestStatus(profileId);
    
    if (interestStatus === 'received') {
      await handleAcceptInterest(profileId);
    } else if (interestStatus === 'none') {
      await handleSwipeInterest(profileId);
    }
  };

  const getInterestStatus = (profileId) => {
    const sentInterest = sentInterests.find(int => 
      int.receiver?.id === profileId || int.receiverId === profileId
    );
    
    const receivedInterest = receivedInterests.find(int =>
      int.sender?.id === profileId || int.senderId === profileId
    );
    
    // Mutual match - either sent or received is accepted
    if (sentInterest?.status === 'accepted' || receivedInterest?.status === 'accepted') {
      return 'mutual';
    }
    
    // Sent interest pending
    if (sentInterest?.status === 'pending') return 'sent';
    
    // Received interest pending
    if (receivedInterest?.status === 'pending') return 'received';
    
    return 'none';
  };

  const handleSendInterest = () => {
    toast({
      title: "Interest Sent",
      description: "Your interest has been sent successfully!",
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Heart className="w-16 h-16 text-coral-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Login Required</h2>
            <p className="text-gray-600 mb-6">Please login to view your shortlisted profiles</p>
            <Link to="/login">
              <Button className="bg-mint-500 hover:bg-mint-600">Login Now</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full px-2">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your shortlist...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full px-2">
        <BackButton />
        
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Shortlist</h1>
          <p className="text-gray-600">
            You have {shortlistedProfiles.length} {shortlistedProfiles.length === 1 ? 'profile' : 'profiles'} in your shortlist
          </p>
        </div>

        {shortlistedProfiles.length === 0 ? (
          <Card>
            <CardContent className="pt-12 pb-12 text-center">
              <Heart className="w-16 h-16 text-coral-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Your shortlist is empty</h2>
              <p className="text-gray-600 mb-6">
                Start adding profiles you're interested in to your shortlist
              </p>
              <Link to="/search">
                <Button className="bg-mint-500 hover:bg-mint-600">Browse Profiles</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {shortlistedProfiles.map((profile) => {
              const interestStatus = getInterestStatus(profile.id);
              return (
                <DynamicProfileCard
                  key={profile.id}
                  profile={profile}
                  onCardClick={() => navigate(`/profile/${profile.id}`)}
                  onInterestClick={() => handleInterestAction(profile.id)}
                  isInterestSent={interestStatus === 'sent'}
                  isInterestReceived={interestStatus === 'received'}
                  isMutualMatch={interestStatus === 'mutual'}
                  isPremium={premiumStatus?.isPremium}
                  hidePremiumBadge={true}
                />
              );
            })}
          </div>
        )}

        {shortlistedProfiles.length > 0 && (
          <div className="mt-8 text-center">
            <Link to="/search">
              <Button variant="outline" className="border-mint-500 text-navy-500 hover:bg-cream-100">
                Browse More Profiles
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShortlistPage;
